import type { NightlySummary, SolverReportResponse } from '@zinc/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api } from '../../lib/api';
import { ScoreChip } from '../../components/ScoreChip';
import { Bar, Panel, smallButton, when } from './parts';

export function minutes(ms: number): string {
  const m = Math.round(ms / 60_000);
  return m < 1 ? 'under a minute' : `${m} min`;
}

/** The Nightly tests card's lines (mockup 08), shared with the overview. */
export function NightlyLines({ n }: { n: NightlySummary | null }) {
  if (!n) return <span className="text-[14px] text-muted">No run yet. It runs at 03:00.</span>;
  if (n.state !== 'done' && n.state !== 'failed') {
    return (
      <>
        <span className="text-[14px]">
          {n.state === 'running' ? 'Running' : 'Waiting to run'} · {Math.round(n.progress * 100)}%
        </span>
        <Bar value={n.progress} />
      </>
    );
  }
  if (n.state === 'failed' || !n.result) {
    return <span className="text-[14px] text-mistake">The run stopped: {n.error}</span>;
  }
  const r = n.result;
  const ok = r.failures.length === 0;
  return (
    <>
      <div className="flex items-center gap-2">
        {ok ? (
          <svg width="26" height="26" viewBox="0 0 16 16" aria-hidden="true">
            <circle cx="8" cy="8" r="7" fill="#1E6B3A" />
            <path
              d="M4.8 8.2l2.1 2.1 4.3-4.4"
              fill="none"
              stroke="#FFFFFF"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : null}
        <span className={`text-[24px] font-bold ${ok ? '' : 'text-mistake'}`}>
          {ok ? 'Passed' : `${r.failures.length} failed`}
        </span>
      </div>
      <span className="text-[14px]">
        {r.passed.toLocaleString('en-GB')} of {r.total.toLocaleString('en-GB')} library puzzles
      </span>
      <span className="text-[13px] text-muted">
        {when(n.finishedAt)} · took {minutes(r.ms)}
      </span>
      <span className="text-[13px] text-muted">
        Logical solver finished {r.logicFinished.toLocaleString('en-GB')}; stalled on{' '}
        {r.stalled.toLocaleString('en-GB')}
      </span>
    </>
  );
}

const KINDS: Record<string, string> = {
  'short-chains': 'Needs short contradiction chains',
  'long-chains': 'Needs long contradiction chains',
  deep: 'Needs a search (deep contradiction)',
  unfinished: 'The logic can’t finish',
};

/** Admin → Solver report: where the logical solver stalls most often, from the nightly run. */
export function AdminSolverReportPage() {
  const client = useQueryClient();
  const data = useQuery({
    queryKey: ['admin', 'solver-report'],
    queryFn: () => api<SolverReportResponse>('/api/admin/solver-report'),
    refetchInterval: (q) => (q.state.data?.running ? 3000 : false),
  });
  const run = useMutation({
    mutationFn: () => api('/api/admin/run-nightly', { method: 'POST' }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['admin', 'solver-report'] }),
  });
  const last = data.data?.last ?? null;
  const result = last?.result ?? null;
  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="m-0 flex-1 text-[26px] font-bold">Solver report</h1>
        <button
          type="button"
          className={smallButton}
          disabled={Boolean(data.data?.running) || run.isPending}
          onClick={() => run.mutate()}
        >
          Run nightly jobs now
        </button>
      </div>
      <p className="m-0 text-[14px] text-muted">
        From the nightly test run over every library puzzle: the ones the logical solver can’t get
        through with its techniques alone, grouped by how it goes on and the rule around the spot.
        The biggest groups are the techniques worth adding.
      </p>
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Last run" small>
          <NightlyLines n={last} />
        </Panel>
        {data.data?.running && (
          <Panel title="Running now" small>
            <NightlyLines n={data.data.running} />
          </Panel>
        )}
      </div>
      {result && result.failures.length > 0 && (
        <Panel title={`Failed: ${result.failures.length}`}>
          <ul className="m-0 flex flex-col gap-1.5 pl-5 text-[13px]">
            {result.failures.map((f) => (
              <li key={f.puzzleId}>
                <Link to={`/library/${f.puzzleId}`}>{f.title}</Link>
                <span className="text-muted"> · {f.problem}</span>
              </li>
            ))}
          </ul>
        </Panel>
      )}
      {result?.slowest && result.slowest.length > 0 && (
        <Panel
          title="Slowest to check"
          side={
            <span className="text-[13px] text-muted">Uniqueness and the full logical solve</span>
          }
        >
          <ul className="m-0 flex flex-col gap-1 p-0">
            {result.slowest.map((p) => (
              <li key={p.id} className="flex items-center gap-2 text-[13px]">
                <Link to={`/library/${p.id}`} className="min-w-0 flex-1 truncate">
                  {p.title}
                </Link>
                <span className="text-muted tabular-nums">{(p.ms / 1000).toFixed(1)} s</span>
              </li>
            ))}
          </ul>
        </Panel>
      )}
      {result?.fog && result.fog.length > 0 && (
        <Panel
          title="Through the fog"
          side={
            <span className="text-[13px] text-muted">
              {result.fog.filter((f) => f.solved).length} of {result.fog.length} finish
            </span>
          }
        >
          <span className="text-[13px] text-muted">
            Each fog puzzle solved from what the player can see, with logic only. Scores don’t
            change yet: the first chip is the puzzle’s score, the second the score through the fog.
          </span>
          <ul className="m-0 flex flex-col gap-1 p-0">
            {result.fog.map((f) => (
              <li key={f.id} className="flex items-center gap-2 text-[13px]">
                <Link to={`/library/${f.id}`} className="min-w-0 flex-1 truncate">
                  {f.title}
                </Link>
                {!f.solved && (
                  <span className="text-muted">
                    stops at {f.placed} of {f.cells} digits, {f.lit} cells lit
                  </span>
                )}
                <ScoreChip score={f.score} />
                {f.solved && <ScoreChip score={f.fogScore} />}
              </li>
            ))}
          </ul>
        </Panel>
      )}
      {result && result.groups.length === 0 && (
        <Panel title="Where the solver stalls">
          <span className="text-[14px] text-muted">
            Nowhere: the logic solved every library puzzle without contradiction chains.
          </span>
        </Panel>
      )}
      {result?.groups.map((g) => (
        <Panel
          key={g.key}
          title={g.label}
          side={
            <span className="text-[13px] text-muted">
              {g.count} {g.count === 1 ? 'puzzle' : 'puzzles'}
            </span>
          }
        >
          <span className="text-[13px] text-muted">{KINDS[g.kind] ?? g.kind}</span>
          <ul className="m-0 flex flex-col gap-1 p-0">
            {g.puzzles.map((p) => (
              <li key={p.id} className="flex items-center gap-2 text-[13px]">
                <Link to={`/library/${p.id}`} className="min-w-0 flex-1 truncate">
                  {p.title}
                </Link>
                <span className="text-muted">
                  stalls at step {p.atStep} of {p.steps}
                </span>
                <ScoreChip score={p.score} />
              </li>
            ))}
            {g.count > g.puzzles.length && (
              <li className="text-[13px] text-muted">and {g.count - g.puzzles.length} more</li>
            )}
          </ul>
        </Panel>
      ))}
    </>
  );
}
